import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import MatterIndicators from 'components/matters/MatterIndicators';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FormattedDate } from 'components/misc/FormattedDate';
import NMoreChip from 'components/util/NMoreChip';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { Tooltip } from 'components/ui/tooltip';
import { ReactNode, useMemo } from 'react';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyGetDebriefingsCountQuery, useLazyGetDebriefingsWithFiltersQuery } from 'store/api/debriefings';
import { useStartDebriefingExportMutation } from 'store/api/exports';
import { EXPORT_CONFIG } from 'types/exports';
import { cn } from 'utils/cn';
import { useAsyncExport } from 'utils/useAsyncExport';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useDebriefingPermissions } from './useDebriefingPermissions';
import { useClassifiers } from 'utils/useClassifiers';
import Loader from 'components/Loader';
import { t } from 'utils/getTranslations';
import { DebriefingAccessIcon } from './components/DebriefingAccessIndicator';
import { DebriefingLessonsCell } from './components/DebriefingLessonsCell';
import { MaskedCell } from './components/DebriefingAccessIndicator';
import { DebriefingsTableVariant, getDebriefingsFilterConfig } from './debriefingsFilterConfig';
import { DEBRIEFING_LESSON_CATEGORY_CLASSIFIER, DebriefingListElement, DebriefingType } from './types';

const USED_CLASSIFIERS = [DEBRIEFING_LESSON_CATEGORY_CLASSIFIER, 'BUSINESS_AREA_TYPE'];

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

/** Rows the viewer cannot open reuse the DataTable's LIMITED-visibility grey background. */
type DebriefingRow = DebriefingListElement & { visibility?: 'FULL' | 'LIMITED' };

/**
 * The row's identity — the case on case-specific rows, otherwise the client, the client-specific
 * placeholder, or the project name. Only a case identity puts the client on a second line beneath
 * it, and only a case identity carries an icon: the padlock of the case itself (in the new design its
 * "Confidential case" chip, on the cell's bottom line with "+N more"). The debriefing's own
 * confidentiality and access sit under the date, which is what they describe.
 */
function CaseClientCell({ row, variant }: { row: DebriefingRow; variant: DebriefingsTableVariant }) {
    const isNewDesign = useIsNewDesign();
    const clientLink =
        variant !== 'client-tab' && row.clientParty ? (
            <RecordLink
                to={`/contact/${row.clientParty.id}`}
                className={cn(LINK_CLASS, 'text-[13px] font-semibold')}
                onClick={(event) => event.stopPropagation()}
                label={row.clientParty.name}
            />
        ) : null;
    const firstMatter = row.matters?.[0];
    const extraMatters = (row.matters?.length ?? 0) - 1;

    let identity: ReactNode;
    if (row.debriefingType === DebriefingType.NON_CLIENT) {
        identity = <span className="text-[13px]">{row.projectName ?? '-'}</span>;
    } else if (firstMatter) {
        const confidential = <MatterIndicators isConfidential={firstMatter.isConfidential} />;
        const matterLink = (
            <RecordLink
                className={LINK_CLASS}
                to={`/matter/${firstMatter.id}`}
                onClick={(event) => event.stopPropagation()}
                label={[firstMatter.caseNumber, firstMatter.name].filter(Boolean).join(' ')}
            />
        );
        const moreMatters =
            extraMatters > 0 ? (
                <Tooltip
                    content={(row.matters ?? [])
                        .slice(1)
                        .map((matter) => [matter.caseNumber, matter.name].filter(Boolean).join(' '))
                        .join(', ')}
                >
                    <NMoreChip count={extraMatters} />
                </Tooltip>
            ) : null;
        if (isNewDesign) {
            // The new design's list cells keep their chips on a line of their own, under the case and its client.
            return (
                <div className="flex min-w-0 flex-col gap-1">
                    {matterLink}
                    {clientLink}
                    {firstMatter.isConfidential || moreMatters ? (
                        <div className="flex flex-wrap items-center gap-1.5">
                            {confidential}
                            {moreMatters}
                        </div>
                    ) : null}
                </div>
            );
        }
        identity = (
            <>
                {confidential}
                {matterLink}
                {moreMatters}
            </>
        );
    } else if (variant === 'client-tab' && row.debriefingType === DebriefingType.CLIENT_SPECIFIC) {
        identity = <span className="text-[13px]">{t('debriefing.columns.client-specific-case-cell')}</span>;
    } else {
        // clientLink is null when the client party was since soft-deleted — reads drop it.
        identity = clientLink ?? <span className="text-[13px]">-</span>;
    }

    const identityLine = <div className="flex items-center gap-1.5">{identity}</div>;
    if (!firstMatter) {
        return identityLine;
    }
    return (
        <div className="flex flex-col">
            {identityLine}
            {clientLink}
        </div>
    );
}

function TasksCountCell({ row }: { row: DebriefingRow }) {
    if (!row.accessible) {
        return <MaskedCell />;
    }
    if (!row.taskTotalCount) {
        return <span className="text-fg-disabled text-[13px]">{t('debriefing.columns.no-tasks')}</span>;
    }
    const allDone = row.taskDoneCount === row.taskTotalCount;
    return (
        <RouterLink
            to={`/debriefing/${row.id}/tasks`}
            className={cn(
                'entity-link w-fit text-[13px] font-semibold no-underline hover:underline',
                // Important, to hold the done/open colour against the table's own link colour, hover included.
                allDone ? 'text-fg-success!' : 'text-fg-warning!'
            )}
            onClick={(event) => event.stopPropagation()}
        >
            {row.taskDoneCount ?? 0}/{row.taskTotalCount}
        </RouterLink>
    );
}

/** The list's count, with the drafts it lists. */
const useLazyDebriefingsCountWithDraftsQuery: typeof useLazyGetDebriefingsCountQuery = (options) => {
    const [count, ...rest] = useLazyGetDebriefingsCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

interface Props {
    variant: DebriefingsTableVariant;
    matterId?: string;
    clientPartyId?: string;
}

const getDebriefingHref = (debriefing: DebriefingRow) => `/debriefing/${debriefing.id}`;

export function DebriefingsTable({ variant, matterId, clientPartyId }: Props) {
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();
    const [fetchDebriefings, { data, isFetching }] = useLazyGetDebriefingsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchDebriefings);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const { canExport } = useDebriefingPermissions();
    const { isBusy: isExportBusy, triggerManualDownload: triggerExport } = useAsyncExport(
        useStartDebriefingExportMutation,
        {
            entityType: 'DEBRIEFING',
            entityName: t('debriefing.export.entity-name'),
            fileName: EXPORT_CONFIG.DEBRIEFING.fileName,
        }
    );

    // The global list keeps its own header action; the tabs seed the new debriefing from their parent.
    const addDebriefingPath =
        variant === 'case-tab' && matterId
            ? `/matter/${matterId}/debriefing/new`
            : variant === 'client-tab' && clientPartyId
              ? `/contact/${clientPartyId}/debriefing/new`
              : undefined;

    const filterConfig = useMemo(
        () =>
            getDebriefingsFilterConfig({
                variant,
                matterId,
                clientPartyId,
                categoryClassifier: classifiers?.[DEBRIEFING_LESSON_CATEGORY_CLASSIFIER],
                businessAreaClassifier: classifiers?.['BUSINESS_AREA_TYPE'],
            }),
        [variant, matterId, clientPartyId, classifiers]
    );

    const columns = useMemo((): DataTableColumnDef<DebriefingRow>[] => {
        const dateColumn: DataTableColumnDef<DebriefingRow> = {
            id: 'debriefingDate',
            accessorKey: 'debriefingDate',
            header: t('date'),
            enableSorting: true,
            // Wide enough for the new design's "Confidential debriefing" chip under the date.
            meta: { widthWeight: 0.7, minPx: isNewDesign ? 200 : 110 },
            cell: ({ row }) => (
                <div className="flex flex-col items-start gap-0.5">
                    {row.original.isDraft ? <DraftChip /> : null}
                    <div className="flex w-full items-start justify-between gap-1">
                        <RouterLink
                            to={`/debriefing/${row.original.id}`}
                            className={cn(LINK_CLASS, 'font-semibold whitespace-nowrap')}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {/* A draft may have no date yet. */}
                            <FormattedDate
                                value={row.original.debriefingDate}
                                fallback={t('debriefing.details.title-prefix')}
                            />
                        </RouterLink>
                        {/* The list has no actions column, so a draft's menu sits by its date. */}
                        {row.original.isDraft ? (
                            <DraftRowMenu draft={{ type: 'debriefing', id: row.original.id }} className="size-7" />
                        ) : null}
                    </div>
                    <DebriefingAccessIcon
                        accessible={row.original.accessible}
                        isConfidential={row.original.isConfidential}
                        accessContacts={row.original.accessContacts}
                    />
                </div>
            ),
        };
        const caseClientColumn: DataTableColumnDef<DebriefingRow> = {
            id: 'caseClient',
            header: t(variant === 'client-tab' ? 'debriefing.columns.case' : 'debriefing.columns.case-client'),
            enableSorting: false,
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => <CaseClientCell row={row.original} variant={variant} />,
        };
        const didWellColumn: DataTableColumnDef<DebriefingRow> = {
            id: 'lessonsDidWell',
            header: t('debriefing.lessons.did-well-heading'),
            enableSorting: false,
            meta: { widthWeight: 2, minPx: 200 },
            cell: ({ row }) => (
                <DebriefingLessonsCell lessons={row.original.lessonsDidWell} accessible={row.original.accessible} />
            ),
        };
        const couldImproveColumn: DataTableColumnDef<DebriefingRow> = {
            id: 'lessonsCouldImprove',
            header: t('debriefing.lessons.could-improve-heading'),
            enableSorting: false,
            meta: { widthWeight: 2, minPx: 200 },
            cell: ({ row }) => (
                <DebriefingLessonsCell
                    lessons={row.original.lessonsCouldImprove}
                    accessible={row.original.accessible}
                />
            ),
        };
        const tasksColumn: DataTableColumnDef<DebriefingRow> = {
            id: 'tasks',
            header: () => (
                <span className="inline-flex items-center gap-1">
                    {t('debriefing.columns.tasks')}
                    <Tooltip content={t('debriefing.columns.tasks-tooltip')}>
                        <InfoOutlinedIcon className="h-3.5 w-3.5" />
                    </Tooltip>
                </span>
            ),
            enableSorting: false,
            meta: { widthWeight: 0.6, minPx: 90, pickerLabel: t('debriefing.columns.tasks') },
            cell: ({ row }) => <TasksCountCell row={row.original} />,
        };
        return variant === 'case-tab'
            ? [dateColumn, didWellColumn, couldImproveColumn, tasksColumn]
            : [dateColumn, caseClientColumn, didWellColumn, couldImproveColumn, tasksColumn];
    }, [variant, isNewDesign]);

    // Restricted rows paint the standard LIMITED grey; they stay fully visible and clickable.
    const dataWithVisibility = useMemo(
        () =>
            data
                ? {
                      ...data,
                      content: data.content.map((row: DebriefingListElement) =>
                          row.accessible ? row : { ...row, visibility: 'LIMITED' as const }
                      ),
                  }
                : undefined,
        [data]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<DebriefingRow>
            getRowHref={getDebriefingHref}
            listKey="debriefings"
            columnConfigId={`debriefings-${variant}`}
            columns={columns}
            data={dataWithVisibility}
            isLoading={isFetching}
            pluralTextId="debriefing.debriefings"
            singularTextId="debriefing.debriefing"
            noEntriesId="debriefing.no-debriefings-found"
            fetchData={fetchData}
            fetchCount={useLazyDebriefingsCountWithDraftsQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            canRememberFilters={variant === 'global'}
            extraHeaderComponent={
                addDebriefingPath ? (
                    <ListCreateButton
                        permissions={['CREATE.DEBRIEFINGS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(addDebriefingPath)}
                        labelId="debriefing.add-debriefing"
                        newLabelId="debriefing.form.new-title"
                    />
                ) : undefined
            }
            exportHookResults={
                variant === 'global' && canExport
                    ? [{ labelKey: 'export', isBusy: isExportBusy, triggerManualDownload: triggerExport }]
                    : undefined
            }
        />
    );
}
